// Pure data helpers shared between income.js (renders the legend/export row
// eagerly, in the page header) and IncomeChart.impl.js (the lazy-loaded
// recharts plot). Kept free of recharts/d3 imports so income.js can use them
// without pulling that ~2MB chunk into the eager bundle.

// `labelKey` overrides the legend copy where the same series needs naming
// differently per context: on a single-product chart the product is already
// implied by the page, but the All chart stacks both and has to say which
// is which. Defaults to `key` when the two agree.
export const STACKS = {
  press: [
    { key: "memberships", fill: "var(--information)" },
    { key: "passes", fill: "var(--primary)" },
  ],
  all: [
    { key: "push", labelKey: "subscriptionsPush", fill: "var(--information)" },
    { key: "press", labelKey: "pressAll", fill: "var(--primary)" },
    { key: "unattributed", fill: "var(--white-30)" },
  ],
  push: [{ key: "push", fill: "var(--information)" }],
};

export const getStacks = (context) => STACKS[context] || STACKS.all;

export const buildChartData = (series) =>
  series.map((bucket) => {
    const pressGross = bucket.pressGross ?? 0;
    const passGross = bucket.passGross ?? 0;
    const pushGross = bucket.pushGross ?? 0;
    return {
      ...bucket,
      memberships: pressGross - passGross,
      passes: passGross,
      push: pushGross,
      press: pressGross,
      unattributed: (bucket.gross ?? 0) - pushGross - pressGross,
      refundsNegative: -(bucket.refunds ?? 0),
    };
  });

export const getChartLegend = (chartData, stacks, t) => {
  const hasRefunds = chartData.some((d) => (d.refunds ?? 0) > 0);
  const items = [
    ...stacks.map(({ key, labelKey, fill }) => ({
      key,
      fill,
      label: t(`income.chartLegend.${labelKey || key}`),
    })),
    ...(hasRefunds
      ? [
          {
            key: "refunds",
            fill: "var(--danger)",
            label: t("income.chartLegend.refunds"),
          },
        ]
      : []),
  ];
  return { hasRefunds, items };
};

export const seriesToCsv = (series, t) => {
  const header = [
    t("income.chartCsv.period"),
    t("income.chartCsv.gross"),
    t("income.chartCsv.net"),
    t("income.chartCsv.refunds"),
  ].join(",");
  const rows = series.map((bucket) =>
    [
      bucket.label,
      bucket.gross ?? 0,
      bucket.net ?? 0,
      bucket.refunds ?? 0,
    ].join(",")
  );
  return [header, ...rows].join("\n");
};

/** Generic CSV: `header` is a list of column labels, `rows` a list of value lists. */
export const rowsToCsv = (header, rows) =>
  [header, ...rows]
    .map((row) =>
      row
        .map((cell) => {
          const text = cell == null ? "" : String(cell);
          return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
        })
        .join(",")
    )
    .join("\n");
